<div class="page-content-wrapper">
  <app-sidebar-layout [showMenuToggle]="true">

    <div class="table-header">
      <div class="card-main-title">
        <app-tab-main [selectedIndex]="selectedTabIndex" [links]="tabs"></app-tab-main>
      </div>
    </div>

    <div class="job-mapping-page">

      <div class="counter-strip" aria-live="polite">
        <div class="progress-track">
          <div class="progress-fill" [style.width.%]="progressPercent"></div>
        </div>
        <div class="counter-text">
          <span class="mapped">{{ mappedCount }} {{ 'mapped' | translate }}</span>
          <span class="divider">/</span>
          <span class="unmapped">{{ unmappedCount }} {{ 'unmapped' | translate }}</span>
          <span class="percent">({{ progressPercent }}%)</span>
        </div>
      </div>

      <div class="two-pane">

        <section class="pane left-pane">
          <div class="pane-header">
            <div class="segmented">
              <button type="button" [class.active]="viewMode === 'unmapped'" (click)="setViewMode('unmapped')">
                {{ 'Unmapped' | translate }} ({{ unmappedCount }})
              </button>
              <button type="button" [class.active]="viewMode === 'mapped'" (click)="setViewMode('mapped')">
                {{ 'Mapped' | translate }} ({{ mappedCount }})
              </button>
            </div>
            <input class="pane-search" [(ngModel)]="leftSearch" (ngModelChange)="onLeftSearchChange()"
              [placeholder]="'Search' | translate" />
            </div>

            <!-- Unmapped view -->
            @if (viewMode === 'unmapped') {
              @if (candidatesLoading) {
                <div class="pane-loading">
                  <mat-spinner diameter="28"></mat-spinner>
                </div>
              }
              @if (candidatesError && !candidatesLoading) {
                <div class="pane-error">
                  <span>{{ 'Failed to load candidates' | translate }}</span>
                  <button mat-button color="primary" (click)="loadCandidates()">{{ 'Retry' | translate }}</button>
                </div>
              }
              @if (!candidatesLoading && !candidatesError && candidateGroupsView.length === 0) {
                <div class="pane-empty">
                  {{ 'No unmapped candidates' | translate }}
                </div>
              }
              @if (!candidatesLoading && !candidatesError) {
                <div class="pane-body">
                  @for (group of candidateGroupsView; track group) {
                    <div class="group">
                      <div class="group-header">{{ group.label }}</div>
                      @for (candidate of group.items; track trackByCandidate($index, candidate)) {
                        <label class="candidate-row">
                          <input type="checkbox" [checked]="isSelected(candidate)" (change)="toggleSelect(candidate)" />
                          <span class="occurrence-badge">{{ candidate.occurrenceCount }}</span>
                          <span class="type-chip">{{ group.label }}</span>
                          <span class="source-value">{{ sourceDisplay(candidate) }}</span>
                        </label>
                      }
                    </div>
                  }
                </div>
              }
            }

            <!-- Mapped view -->
            @if (viewMode === 'mapped') {
              @if (mappingsLoading) {
                <div class="pane-loading">
                  <mat-spinner diameter="28"></mat-spinner>
                </div>
              }
              @if (mappingsError && !mappingsLoading) {
                <div class="pane-error">
                  <span>{{ 'Failed to load mappings' | translate }}</span>
                  <button mat-button color="primary" (click)="loadMappings()">{{ 'Retry' | translate }}</button>
                </div>
              }
              @if (!mappingsLoading && !mappingsError && mappedGroupsView.length === 0) {
                <div class="pane-empty">
                  {{ 'No mappings yet' | translate }}
                </div>
              }
              @if (!mappingsLoading && !mappingsError) {
                <div class="pane-body">
                  @for (group of mappedGroupsView; track group) {
                    <div class="group">
                      <div class="group-header">{{ group.label }}</div>
                      @for (mapping of group.items; track trackByMapping($index, mapping)) {
                        <div class="mapped-row">
                          <span class="type-chip">{{ group.label }}</span>
                          <span class="source-value">{{ sourceDisplay(mapping) }}</span>
                          <span class="mapped-grouping">{{ groupingName(mapping.jobDictionaryId) }}</span>
                          <button mat-icon-button [matMenuTriggerFor]="rowMenu" class="kebab">
                            <mat-icon>more_vert</mat-icon>
                          </button>
                          <mat-menu #rowMenu="matMenu">
                            <button mat-menu-item [matMenuTriggerFor]="changeMenu" [disabled]="!canWrite">{{ 'Change mapping' | translate }}</button>
                            <button mat-menu-item (click)="removeMapping(mapping)" [disabled]="!canWrite">{{ 'Remove mapping' | translate }}</button>
                            <mat-menu #changeMenu="matMenu">
                              @for (grouping of groupings; track trackByGrouping($index, grouping)) {
                                <button mat-menu-item
                                  (click)="changeMapping(mapping, grouping)">
                                  {{ grouping.name }}
                                </button>
                              }
                            </mat-menu>
                          </mat-menu>
                        </div>
                      }
                    </div>
                  }
                </div>
              }
            }
          </section>

          <section class="pane right-pane">
            <div class="pane-header">
              <input class="pane-search" [(ngModel)]="rightSearch" (ngModelChange)="onRightSearchChange()"
                [placeholder]="'Search groupings' | translate" />
                <button mat-stroked-button color="primary" type="button" (click)="newGrouping()" [disabled]="!canWrite">
                  + {{ 'New Grouping' | translate }}
                </button>
              </div>

              @if (selectedCount > 0) {
                <div class="selection-bar">
                  <span class="selected-count">{{ selectedCount }} {{ 'selected' | translate }}</span>
                  <button mat-button type="button" (click)="clearSelection()">{{ 'Clear' | translate }}</button>
                </div>
              }

              @if (groupingsLoading) {
                <div class="pane-loading">
                  <mat-spinner diameter="28"></mat-spinner>
                </div>
              }
              @if (groupingsError && !groupingsLoading) {
                <div class="pane-error">
                  <span>{{ 'Failed to load groupings' | translate }}</span>
                  <button mat-button color="primary" (click)="loadGroupings()">{{ 'Retry' | translate }}</button>
                </div>
              }
              @if (!groupingsLoading && !groupingsError && filteredGroupingsView.length === 0) {
                <div class="pane-empty">
                  {{ 'No groupings found' | translate }}
                </div>
              }
              @if (!groupingsLoading && !groupingsError) {
                <div class="pane-body">
                  @for (grouping of filteredGroupingsView; track trackByGrouping($index, grouping)) {
                    <div class="grouping-item">
                      <div class="grouping-info">
                        <span class="grouping-name">{{ grouping.name }}</span>
                        @if (grouping.code) {
                          <span class="grouping-code">{{ grouping.code }}</span>
                        }
                      </div>
                      <span class="grouping-count">{{ mappingCountFor(grouping.id) }} {{ 'mapped' | translate }}</span>
                      <button mat-flat-button color="primary" type="button"
                        [disabled]="selectedCount === 0 || !canWrite"
                        (click)="assignTo(grouping)">
                        {{ 'Assign' | translate }}
                      </button>
                    </div>
                  }
                </div>
              }
            </section>

          </div>
        </div>

      </app-sidebar-layout>
    </div>
